import React, { useEffect, useRef, useState } from 'react'

import debounce from 'lodash/debounce'
import moment, { Moment } from 'moment'

import { DriftDatePicker, SingleDatePicker } from '@driftt/react-dates'
import { RANGE_TYPES } from '@driftt/react-dates/constants'

import usePrevious from '../../hooks/usePrevious'

import styles from './styles.module.css'

const CALENDAR_CELL_SIZE = 30

// date helpers
const isSameDay = (a: Moment, b: Moment) => {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false
  return a.date() === b.date() && a.month() === b.month() && a.year() === b.year()
}

const isAfterDay = (a: Moment, b: Moment) => {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false
  return !isBeforeDay(a, b) && !isSameDay(a, b)
}

const isBeforeDay = (a: Moment, b: Moment) => {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false

  const aYear = a.year()
  const aMonth = a.month()

  const bYear = b.year()
  const bMonth = b.month()

  const isSameYear = aYear === bYear
  const isSameMonth = aMonth === bMonth

  if (isSameYear && isSameMonth) return a.date() < b.date()
  if (isSameYear) return aMonth < bMonth
  return aYear < bYear
}

const isInclusivelyBeforeDay = (a: Moment, b: Moment) => {
  if (!moment.isMoment(a) || !moment.isMoment(b)) return false
  return !isAfterDay(a, b)
}

const isInRange = (day: Moment) => isInclusivelyBeforeDay(day, moment())

// end of date helpers

export interface DateRange {
  startDate: Moment
  endDate: Moment
  type?: string
}

const RANGE_SELECTORS = {
  [RANGE_TYPES.SEVEN_DAYS]: {
    type: RANGE_TYPES.SEVEN_DAYS,
    endDate: moment().endOf('day'),
    startDate: moment()
      .startOf('day')
      .subtract(7, 'days')
  },
  [RANGE_TYPES.THIRTY_DAYS]: {
    type: RANGE_TYPES.THIRTY_DAYS,
    endDate: moment().endOf('day'),
    startDate: moment()
      .startOf('day')
      .subtract(30, 'days')
  },
  [RANGE_TYPES.THREE_MONTHS]: {
    type: RANGE_TYPES.THREE_MONTHS,
    endDate: moment().endOf('day'),
    startDate: moment()
      .startOf('day')
      .subtract(3, 'month')
  }
}

const defaultDateRange = {
  endDate: moment().endOf('day'),
  startDate: moment()
    .startOf('day')
    .subtract(1, 'month')
}

export const getDefaultRange = () => defaultDateRange

export const getDefaultRangeFor = (dateRange: DateRange, type?: any) => {
  return type ? RANGE_SELECTORS[type] || dateRange : defaultDateRange
}

export const getDefaultDate = () => moment().endOf('day')

export const toTimestamp = (dateRange: DateRange | null | undefined) => {
  return {
    startDateTs: dateRange?.startDate?.valueOf(),
    endDateTs: dateRange?.endDate?.valueOf()
  }
}

export const fromTimestamp = (dateRange?: { startDateTs: number; endDateTs: number }) => {
  return {
    startDate: dateRange?.startDateTs ? moment(dateRange.startDateTs) : undefined,
    endDate: dateRange?.endDateTs ? moment(dateRange.endDateTs) : undefined
  }
}

export const DateRangePicker = ({
  onDateRangeChange = () => {},
  dateRange = defaultDateRange,
  openDirection = 'down'
}: {
  onDateRangeChange?: (dateRange: DateRange) => void
  dateRange?: DateRange
  openDirection?: 'up' | 'down'
}) => {
  const [dateRangeType, setDateRangeTypeLocalState] = useState(dateRange.type)
  const [dateRangeLocalState, setDateRangeLocalState] = useState(dateRange)
  const [focusedInput, setFocusedInput] = useState<string | null>(null)
  const prevDateRange = usePrevious(dateRangeLocalState)

  const onDateRangeChangeDebounced = useRef(debounce(onDateRangeChange, 500))

  const onDatesChange = (dateRange: DateRange) => {
    setDateRangeLocalState({
      startDate: dateRange.startDate?.startOf('day'),
      endDate: dateRange.endDate?.endOf('day')
    })
  }

  useEffect(() => {
    if (prevDateRange && prevDateRange !== dateRangeLocalState) {
      onDateRangeChangeDebounced.current({
        startDate: dateRangeLocalState.startDate?.startOf('day'),
        endDate: dateRangeLocalState.endDate?.endOf('day'),
        type: dateRangeType
      })
    }
  }, [dateRangeType, dateRangeLocalState, prevDateRange])

  return (
    <DriftDatePicker
      startDatePlaceholderText={'Start date'}
      endDatePlaceholderText={'End date'}
      startDate={dateRangeLocalState.startDate}
      startDateId={`startDateId-${Date.now()}`}
      endDate={dateRangeLocalState.endDate}
      endDateId={`endDateId-${Date.now()}`}
      onDatesChange={onDatesChange}
      onFocusChange={(focusedInput: string) => setFocusedInput(focusedInput)}
      focusedInput={focusedInput}
      enableOutsideDays={true}
      allowSingleDateSelection={true}
      showQuickSelects={true}
      onQuickSelect={(value: string) => setDateRangeTypeLocalState(value)}
      disableScroll={false}
      numberOfMonths={2}
      openDirection={openDirection}
      isOutsideRange={(date: Moment) => !isInRange(date)}
      daySize={CALENDAR_CELL_SIZE}
    />
  )
}

export const DatePicker = ({
  onChangeDate = () => {},
  date = moment().endOf('day'),
  openDirection = 'down'
}: {
  onChangeDate?: (date: Moment) => void
  date?: Moment
  openDirection?: 'up' | 'down'
}) => {
  const [dateLocalState, setDateLocalState] = useState<Moment>(date)

  const [focused, setFocused] = useState<boolean>(false)

  const onDateChangeDebounced = useRef(debounce(onChangeDate, 1000))

  const onDateChange = (date: Moment) => {
    setDateLocalState(date)
    onDateChangeDebounced.current(date)
  }

  return (
    <div className={styles.Calendar}>
      <SingleDatePicker
        date={moment.isMoment(dateLocalState) ? dateLocalState : null}
        id={`dateId-${Date.now()}`}
        onDateChange={onDateChange}
        onFocusChange={() => setFocused(!focused)}
        focused={focused}
        enableOutsideDays={true}
        disableScroll={false}
        openDirection={openDirection}
        numberOfMonths={1}
        isOutsideRange={(date: Moment) => !isInRange(date)}
        hideKeyboardShortcutsPanel
        small={true}
        daySize={CALENDAR_CELL_SIZE}
      />
    </div>
  )
}
